/**
 * Illustrations of the public pages, drawn with the app's own interface rather than images: a
 * campaign with its result and a claimed payout, how a budget settles, a creator's payout, and a
 * published result checked with `morcast-verify`. Every figure is an example and says so. Its
 * amounts follow the settlement rules (80% of what the brand pays goes to creators, 20% to
 * Morcast, and the rest of the budget back to the brand). They follow the page's theme, except
 * the terminal, which is dark in both.
 */

import { BadgeCheck, Check, Clock } from "lucide-react";

import { glass, inset } from "../../components/marketing.js";

/** The small label above an illustration or a table of figures, such as "Example". */
export const exampleLabel =
  "text-[0.6875rem] font-semibold tracking-[0.14em] text-zinc-500 uppercase dark:text-zinc-400";
const strong = "text-zinc-950 dark:text-white";
const muted = "text-zinc-500 dark:text-zinc-400";
const body = "text-zinc-600 dark:text-zinc-400";

/** Creators of the example result: their recognized reach and payout at 0.004 USDC each. */
const exampleCreators = [
  {
    initial: "M",
    handle: "@maya",
    reach: "1,130,000",
    payout: "4,520 USDC",
    tone: "from-sky-400 to-indigo-500",
  },
  {
    initial: "L",
    handle: "@leo",
    reach: "450,000",
    payout: "1,800 USDC",
    tone: "from-violet-400 to-fuchsia-500",
  },
  {
    initial: "A",
    handle: "@ana",
    reach: "Below threshold",
    payout: "0 USDC",
    tone: "from-amber-300 to-orange-500",
  },
];

/**
 * The hero's illustration: a campaign open to creators, its published result, and a creator's
 * claimed payout. A 10,000 USDC budget for 2,000,000 impressions, 1,580,000 recognized.
 */
export function ProductPreview() {
  return (
    <div
      role="img"
      aria-label="Example of a campaign open to creators, with its published result and a creator's claimed payout"
      className="relative mx-auto w-full max-w-xl lg:max-w-none"
    >
      <div aria-hidden>
        <div className="pointer-events-none absolute -inset-10 -z-10 rounded-full bg-gradient-to-tr from-brand-300/30 via-brand-200/20 to-brand-400/30 blur-3xl dark:from-brand-400/[0.06] dark:via-white/[0.02] dark:to-brand-400/[0.05]" />

        <div
          className={`ring-gradient relative p-5 sm:ml-auto sm:w-[90%] sm:p-6 sm:pb-20 dark:bg-ink-900/70 ${glass}`}
        >
          <div className="flex items-center justify-between gap-3">
            <span className={`flex min-w-0 items-center gap-2.5 text-sm ${body}`}>
              <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-zinc-800 to-zinc-950 font-display text-xs font-bold text-white dark:from-zinc-100 dark:to-zinc-300 dark:text-ink-950">
                YB
              </span>
              <span className="truncate">Your brand · X</span>
            </span>
            <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-emerald-500/[0.08] px-2.5 py-1 text-xs font-medium text-emerald-800 ring-1 ring-emerald-600/20 ring-inset dark:bg-emerald-400/10 dark:text-emerald-300 dark:ring-emerald-400/25">
              <span className="size-1.5 rounded-full bg-emerald-500 motion-safe:animate-pulse dark:bg-emerald-400" />
              Open now
            </span>
          </div>
          <p className={`mt-5 font-display text-xl font-semibold tracking-[-0.02em] ${strong}`}>
            Cold brew launch
          </p>
          <p className={`mt-1.5 text-sm ${body}`}>
            Show how you make your morning cold brew, and where to get it.
          </p>
          <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
            <div className={`p-3 ${inset}`}>
              <p className={`text-xs ${muted}`}>Creators share up to</p>
              <p className={`mt-1 font-display font-semibold tabular-nums ${strong}`}>8,000 USDC</p>
            </div>
            <div className={`p-3 ${inset}`}>
              <p className={`text-xs ${muted}`}>Target</p>
              <p className={`mt-1 font-display font-semibold tabular-nums ${strong}`}>
                2,000,000 <span className={`font-sans font-normal ${muted}`}>impressions</span>
              </p>
            </div>
          </div>
          <p className={`mt-5 flex items-center gap-1.5 text-xs font-medium ${muted}`}>
            <Clock className="size-3.5" />
            Open for 6 more days · 3 submissions per account
          </p>
        </div>

        <div
          className={`relative mt-4 p-5 sm:-mt-14 sm:w-[23rem] motion-safe:animate-float lg:-ml-6 dark:bg-ink-850/90 ${glass}`}
        >
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className={`font-display font-semibold ${strong}`}>Result</span>
            <span className={`inline-flex items-center gap-1 text-xs ${body}`}>
              <BadgeCheck className="size-3.5 text-emerald-600 dark:text-emerald-400" />
              Published with evidence
            </span>
          </div>
          <div className="mt-4 h-2 overflow-hidden rounded-full bg-zinc-900/[0.07] dark:bg-white/[0.06]">
            <div className="h-full w-[79%] rounded-full bg-gradient-to-r from-brand-600 via-brand-500 to-brand-300 shadow-[0_0_16px_rgb(103_194_222/0.5)] dark:shadow-none" />
          </div>
          <p className={`mt-2 text-xs ${muted}`}>1,580,000 of 2,000,000 impressions recognized</p>
          <ul className="mt-4 space-y-3 text-sm">
            {exampleCreators.map((creator) => (
              <li key={creator.handle} className="flex items-center justify-between gap-3">
                <span className="flex min-w-0 items-center gap-2.5">
                  <span
                    className={`grid size-7 shrink-0 place-items-center rounded-full bg-gradient-to-br text-xs font-semibold text-white ${creator.tone}`}
                  >
                    {creator.initial}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-zinc-800 dark:text-zinc-200">
                      {creator.handle}
                    </span>
                    <span className={`block truncate text-xs ${muted}`}>{creator.reach}</span>
                  </span>
                </span>
                <span className={`shrink-0 font-display font-medium tabular-nums ${strong}`}>
                  {creator.payout}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="absolute right-0 bottom-16 hidden items-center gap-2.5 rounded-full bg-white/90 py-2 pr-4 pl-2 text-sm font-medium text-zinc-900 shadow-xl ring-1 shadow-zinc-900/15 ring-zinc-900/10 backdrop-blur-xl [animation-delay:-3s] motion-safe:animate-float sm:flex dark:bg-ink-850/90 dark:text-white dark:shadow-black/60 dark:ring-white/10">
          <span className="grid size-6 place-items-center rounded-full bg-gradient-to-br from-emerald-300 to-emerald-500 text-ink-950 motion-safe:animate-pulse-ring">
            <Check className="size-3.5" strokeWidth={3} />
          </span>
          4,520 USDC claimed
        </div>
      </div>
    </div>
  );
}

/** An example settlement, so that the pricing reads at a glance. */
export function SettlementExample() {
  const bars = [
    {
      label: "Creators (80%)",
      amount: "6,000 USDC",
      share: 60,
      tone: "bg-gradient-to-r from-brand-600 to-brand-400",
    },
    {
      label: "Morcast (20%)",
      amount: "1,500 USDC",
      share: 15,
      tone: "bg-gradient-to-r from-brand-300 to-brand-200",
    },
    {
      label: "Back to the brand",
      amount: "2,500 USDC",
      share: 25,
      tone: "bg-zinc-300 dark:bg-white/15",
    },
  ];
  return (
    <figure className={`p-5 text-sm sm:p-6 ${glass}`}>
      <figcaption className={exampleLabel}>Example</figcaption>
      <p className={`mt-2 font-display font-semibold ${strong}`}>
        A 10,000 USDC campaign reaching 75% of its target
      </p>
      <div aria-hidden className="mt-5 flex h-3 gap-1 overflow-hidden rounded-full">
        {bars.map((bar) => (
          <span
            key={bar.label}
            className={`rounded-full ${bar.tone}`}
            style={{ width: `${bar.share}%` }}
          />
        ))}
      </div>
      <dl className="mt-5 space-y-2.5">
        {bars.map((bar) => (
          <div key={bar.label} className="flex items-center justify-between gap-3">
            <dt className={`flex items-center gap-2 ${body}`}>
              <span aria-hidden className={`size-2.5 rounded-full ${bar.tone}`} />
              {bar.label}
            </dt>
            <dd className={`font-display font-medium tabular-nums ${strong}`}>{bar.amount}</dd>
          </div>
        ))}
      </dl>
      <p
        className={`mt-5 border-t border-zinc-900/[0.07] pt-4 text-xs leading-5 dark:border-white/[0.08] ${muted}`}
      >
        The brand pays 7,500 USDC for the reach recognized. The rest of the escrowed budget goes
        back.
      </p>
    </figure>
  );
}

/** An example of a creator's payout, ready to claim from the escrow. */
export function PayoutExample() {
  return (
    <figure className={`p-5 text-sm sm:p-6 ${glass}`}>
      <figcaption className={exampleLabel}>Example</figcaption>
      <p className={`mt-2 font-display font-semibold ${strong}`}>
        A creator's payout from one campaign
      </p>
      <div className="mt-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p
            className={`font-display text-4xl font-semibold tracking-[-0.03em] tabular-nums ${strong}`}
          >
            4,520 <span className={`text-lg font-medium ${muted}`}>USDC</span>
          </p>
          <p className={`mt-1 text-xs ${muted}`}>For 1,130,000 impressions recognized</p>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/[0.08] px-3 py-1.5 text-xs font-semibold text-emerald-800 ring-1 ring-emerald-600/20 ring-inset dark:bg-emerald-400/10 dark:text-emerald-300 dark:ring-emerald-400/25">
          <Check aria-hidden className="size-3.5" strokeWidth={3} />
          Ready to claim
        </span>
      </div>
      <p
        className={`mt-5 border-t border-zinc-900/[0.07] pt-4 text-xs leading-5 dark:border-white/[0.08] ${muted}`}
      >
        Claimed straight from the escrow to the creator's wallet, whenever they like. Payouts never
        expire.
      </p>
    </figure>
  );
}

/** What `morcast-verify` prints for a settled campaign's published result: a dark terminal. */
export function VerifyExample() {
  const lines: [string, string][] = [
    ["Campaign", "1 on chain 8453, escrow 0x9fe4…a6e0"],
    ["Revision", "0"],
    ["resultHash", "0x1b1b…b1b1"],
    ["merkleRoot", "0x5e5e…5e5e"],
    ["Recognized", "1580000 of target 2000000"],
    ["Split", "spent 7900000000, fee 1580000000, pool 6320000000, refund 2100000000"],
    ["Dataset", "valid, with 4 items, 3 creators and 2 payouts"],
    ["On-chain", "Settled, and the dataset matches the campaign"],
  ];
  return (
    <figure className="relative">
      <div
        aria-hidden
        className="pointer-events-none absolute -inset-6 -z-10 rounded-[2.5rem] bg-gradient-to-br from-brand-400/25 via-transparent to-brand-600/20 blur-2xl dark:from-white/[0.04] dark:to-brand-400/[0.04]"
      />
      <div className="dark ring-gradient relative overflow-hidden rounded-2xl bg-ink-950 shadow-2xl shadow-black/40">
        <figcaption className="flex items-center gap-2 border-b border-white/[0.07] bg-white/[0.02] px-4 py-3">
          <span aria-hidden className="size-3 rounded-full bg-[#ff5f57]/80" />
          <span aria-hidden className="size-3 rounded-full bg-[#febc2e]/80" />
          <span aria-hidden className="size-3 rounded-full bg-[#28c840]/80" />
          <span className="ml-2 font-mono text-xs text-zinc-400">Example · morcast-verify</span>
        </figcaption>
        <div className="space-y-1 p-5 font-mono text-[11px] leading-6 text-zinc-200 sm:p-6 sm:text-[13px]">
          <p className="break-all">
            <span className="text-emerald-400">$</span> morcast-verify result.json --rpc-url
            https://mainnet.base.org
          </p>
          <dl className="grid grid-cols-[11ch_minmax(0,1fr)] gap-x-3">
            {lines.map(([label, value]) => (
              <div key={label} className="contents">
                <dt className="text-zinc-400">{label}</dt>
                <dd
                  className={`break-words ${label === "On-chain" || label === "Dataset" ? "text-emerald-300" : ""}`}
                >
                  {value}
                </dd>
              </div>
            ))}
          </dl>
          <p aria-hidden className="pt-1">
            <span className="text-emerald-400">$</span>{" "}
            <span className="inline-block h-4 w-2 translate-y-0.5 bg-emerald-400/80 motion-safe:animate-pulse" />
          </p>
        </div>
      </div>
    </figure>
  );
}
